import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "enterprise-data-reconciliation",
  kind: "horizontal",
  title: "Enterprise Data Reconciliation Systems",
  shortTitle: "Data Reconciliation",
  status: "draft",
  tier: "we-do",
  seoTitle: "Enterprise Data Reconciliation Systems | GA",
  seoDescription:
    "Multi-source ingestion pipelines, boundary data contracts, schema drift detection, and entity reconciliation that establish authoritative enterprise records.",
  summary:
    "Ingestion pipelines with boundary data contracts, schema drift detection, entity resolution, and automated reconciliation that establish trustworthy records.",
  image: "/images/blog/reconciling-fragmented-data-hero.webp",
  relatedCapabilitySlugs: [
    "data-search-knowledge",
    "software-architecture-engineering",
  ],
  relatedIndustrySlugs: ["enterprise", "energy", "healthcare"],
  relatedPostSlugs: [
    "reconciling-fragmented-data",
    "reconciling-fragmented-enterprise-identifiers",
  ],
  engineering: [
    "Boundary data contracts and schema validation pipelines preventing silent drift",
    "Entity resolution and record matching across disparate enterprise systems",
    "Authoritative source-of-truth rules and conflicting-field arbitration logic",
    "Automated golden-record synthesis preserving data lineage and field provenance",
    "Exception and quarantine queues with operator review and correction interfaces",
    "Observability, telemetry, and reconciliation health metrics across data flows",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "Integration Does Not Create Agreement",
    },
    {
      type: "paragraph",
      text: "Connecting multiple enterprise systems only moves disagreement faster when schemas, record ownership, and update semantics remain undefined. Pipelines may run without transport errors while downstream reports, operational dashboards, and search indexes quietly corrupt because disparate platforms hold competing versions of the same entity.",
    },
    {
      type: "heading",
      level: 2,
      text: "Data Contracts at the Ingestion Boundary",
    },
    {
      type: "paragraph",
      text: "Reliable data platforms treat integration as an explicit contract. By validating incoming records against strict boundary schemas, detecting drift immediately, and routing unresolvable conflicts into reviewable exception queues, the system guarantees that downstream applications operate on trusted, authoritative data.",
    },
  ],
  phases: {
    study: {
      focus:
        "Map source systems, identity schemes, conflicting fields, and business authority rules across the enterprise data landscape.",
      produces: [
        "System-of-record inventory and authority ownership matrix",
        "Entity identity, key mapping, and schema overlap analysis",
        "Data conflict, drift, and exception frequency assessment",
        "Integration boundary and downstream consumer requirements",
      ],
      fit: {
        good: [
          "Organizations maintaining multiple applications that disagree on customer, asset, vendor, or operational entities",
          "Teams whose reporting or analytics are compromised by silent schema drift, unvalidated ingestion, or duplicate records",
        ],
        poor: [
          "Single-database applications with no external source integration or entity fragmentation",
          "Basic point-to-point ETL scripts where data quality and source authority carry no operational consequence",
        ],
      },
    },
    "initial-design": {
      focus:
        "Architect the canonical data model, contract validation boundaries, and reconciliation arbitration state machine.",
      produces: [
        "Canonical entity schema and data contract specifications",
        "Arbitration rules and field-level authority hierarchy design",
        "Entity matching, deduplication, and resolution algorithms",
        "Quarantine and exception management workflow architecture",
      ],
    },
    "detailed-design": {
      focus:
        "Specify boundary validators, reconciliation pipeline jobs, exception review interfaces, and observability dashboards.",
      produces: [
        "Ingestion endpoint and contract enforcement specifications",
        "Deduplication and entity linkage pipeline contracts",
        "Exception triage and manual resolution interface designs",
        "Data lineage tracking and reconciliation telemetry schemas",
      ],
    },
    implementation: {
      focus:
        "Build, test, and deploy the reconciliation pipelines, boundary validators, and operator review tooling against live enterprise data feeds.",
      produces: [
        "Operational boundary validation and data ingestion pipelines",
        "Automated entity reconciliation and golden-record synthesis services",
        "Operator exception review and resolution interfaces",
        "Pipeline observability, alert rules, and operational runbooks",
      ],
    },
  },
};
